/**
 * UI / lifecycle reducer slice. Handles:
 *   ui.ModuleUnlocked       — modulesUnlocked Set
 *   ui.PillarTransition     — pillarFill
 *   ui.ScreenChanged        — currentScreen
 *   ui.ActChanged           — currentAct
 *   ui.SplitScreenHandshake — splitScreenHandshakes
 *   scenario.Started        — per-trade projection reset
 *   scenario.Completed      — finalise partial pillar fills
 *   projection.Reset        — M5 seek-replay protocol
 *   agent.Spoke             — agent dialogue bubble buffer
 *
 * Pure function: `(state, ev) => state`. Returns the same reference if
 * the event isn't UI-domain (the dispatcher skips a copy in that case).
 */

import type { CdmEvent, EventLogState } from "@/lib/sse/useSSE";
import { initialState } from "@/lib/sse/useSSE";
import { ALL_MODULES } from "@/lib/modules";

export function uiReducer(state: EventLogState, ev: CdmEvent): EventLogState {
  switch (ev.eventType) {
    case "ui.ModuleUnlocked": {
      const m = ev.payload.module as string;
      if (!m) return state;
      const next = new Set(state.modulesUnlocked);
      next.add(m);
      return { ...state, modulesUnlocked: next };
    }
    case "ui.PillarTransition": {
      const next = { ...state.pillarFill };
      const to = (ev.payload.to as string)?.toLowerCase() as keyof typeof next;
      const from = (ev.payload.from as string)?.toLowerCase() as keyof typeof next;
      if (from && from in next) next[from] = 1;
      if (to && to in next) next[to] = 0.05;
      return { ...state, pillarFill: next };
    }
    case "scenario.Completed": {
      // Finalise any partially-lit pillar (>0 and <1) to 1.0. The trade
      // scenarios emit verify→trace and trace→finance transitions during
      // the run, but no closing "finance complete" event — so finance
      // would otherwise stay stuck at the 0.05 "just started glowing"
      // value forever. This fires at the end of every scenario, fixing
      // T1's third ring and giving T2/T3 a clean fully-lit state too.
      const next = { ...state.pillarFill };
      for (const k of ["verify", "trace", "finance"] as const) {
        if (next[k] > 0 && next[k] < 1) next[k] = 1;
      }
      return { ...state, pillarFill: next };
    }
    case "ui.ScreenChanged": {
      return {
        ...state,
        currentScreen: {
          screen:      ev.payload.screen as string,
          perspective: ev.payload.perspective as string,
          label:       ev.payload.label as string,
          changedAt:   new Date(ev.occurredAt).getTime(),
        },
      };
    }
    case "ui.ActChanged": {
      return {
        ...state,
        currentAct: {
          act:   ev.payload.act as number,
          label: ev.payload.label as string,
        },
      };
    }
    case "ui.SplitScreenHandshake": {
      const n = ev.payload.handshake as number;
      if (typeof n !== "number") return state;
      return { ...state, splitScreenHandshakes: [...state.splitScreenHandshakes, n] };
    }
    case "projection.Reset": {
      // M5 — restore initialState (preserving the events audit trail).
      // The events array already includes the projection.Reset marker
      // because the top-level dispatcher appended it before this slice
      // runs. Don't lose that audit row.
      return { ...initialState, events: state.events };
    }
    case "scenario.Started": {
      const sid = (ev.payload?.scenarioId as string | undefined) ?? "";
      if (sid !== "t1-onboarding" && sid !== "t2-bank-financed" && sid !== "t3-reverse-corridor") {
        return state;
      }
      // Per-trade projection reset. Reputation + events array deliberately
      // preserved (audit trail). The other reducers (documents / finance /
      // logistics / compliance) own their own slices; we reset them all
      // here because scenario.Started is a single boundary point.
      const moduleSet = sid === "t1-onboarding"
        ? new Set<string>()
        : new Set<string>(ALL_MODULES);
      const pillarFill = sid === "t1-onboarding"
        ? { verify: 0, trace: 0, finance: 0 }
        : state.pillarFill;
      return {
        ...state,
        contract: null,
        documents: {},
        reconciliation: { status: "idle", cells: {}, weightToleranceAutoResolved: false },
        logisticsMilestones: [],
        customsCleared: { dubaiTrade: false, moccae: false },
        complianceSnapshot: null,
        lcComposer: { open: false, tenorOptionsDays: [30, 60, 120], tenorIndependent: true },
        fundingMix: null,
        lcIssuance: null,
        settlementLegs: [],
        threeFieldMatched: null,
        modulesUnlocked: moduleSet,
        pillarFill,
      };
    }
    case "agent.Spoke": {
      return {
        ...state,
        agentLines: [
          ...state.agentLines.slice(-7), // keep last 8 — bubbles fade after a few seconds
          {
            id: ev.eventId,
            agent: ev.actor.id,
            line: (ev.payload.line as string) ?? "",
            lineKey: (ev.payload.lineKey as string | undefined),
            occurredAt: new Date(ev.occurredAt).getTime(),
          },
        ],
      };
    }
    default:
      return state;
  }
}
